import { Unsubscribe } from "@firebase/util";
import { EDiscountType, EMeasurementStatus } from "api/enums";
import { IMeasurementListRow } from "components/interfaces/IMeasurementListRow";
import { lang } from "i18nLang";
import { makeAutoObservable } from "mobx";
import {
  decimal,
  subtract,
  sum,
  valueToPercentual,
} from "utils/DecimalPrecision";
import { ContractStore, MeasurementStore } from "..";
import MeasurementModel from "../../api/models/MeasurementModel";
import MeasurementRowModel from "../../api/models/MeasurementRowModel";
import WBSModel from "../../api/models/WBSModel";
import MeasurementRowService from "../../api/services/MeasurementRowService";
import MeasurementService from "../../api/services/MeasurementService";

class Store {
  private _measurementsListener: Unsubscribe | undefined = undefined;
  private _measurements: MeasurementModel[] = [];
  private _rowsLists: WBSModel<MeasurementRowModel>[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  public get rows(): IMeasurementListRow[] {
    const contract = ContractStore.getCurrentContract();
    const contractTotal = ContractStore.currentContractTotal;
    let accumulatedTotal: number = 0;
    let accumulatedPercentual: number = 0;

    if (!contract) return [];

    return this._measurements
      .map((measurement) => {
        // Calculando soma do acumulado
        const measuredPercentual = valueToPercentual(
          measurement.total,
          contractTotal
        );
        accumulatedPercentual += measuredPercentual;

        // Calculando soma do total medido
        accumulatedTotal = sum(accumulatedTotal, measurement.total);

        const technicalRetentionPercent =
          measurement.getContract()?.technicalRetention?.percentual || 0;
        const taxesTitle = this.taxesTitle(measurement);
        const taxesValues = measurement.getValuesOfEachTaxe();

        return {
          uid: measurement.uid,
          description: measurement.description,
          initialDate: measurement.initialDate,
          finalDate: measurement.finalDate,
          status: measurement.status,
          totalMeasured: measurement.total,
          readjustValue: measurement.readjustValue,
          total: measurement.getReadjustedTotal(),
          allDiscountsTotal: measurement.getAllDiscountsTotal(),
          discountsTotal: measurement.getDiscountsTotal(),
          taxesTotal: measurement.getTaxesTotal(),
          retentionAdvance: sum(
            measurement.getAdvancesTotal(),
            measurement.getTechnicalRetentionTotal()
          ),
          retentionTotal: measurement.getTechnicalRetentionTotal(),
          technicalRetentionPercent,
          advancesTotal: measurement.getAdvancesTotal(),
          billingsTotal: measurement.getBillingsTotal(),
          receivableTotal: measurement.getNetTotal(),
          balanceToMeasure: subtract(contractTotal, accumulatedTotal),
          measuredPercentual,
          accumulatedPercentual,
          disableEditBilling: !measurement.canEdit(),
          disableShowBilling: !measurement.hasBillingData,
          billingOrder: this.formatBillingOrderDetails(measurement),
          grossTotal: measurement.getGrossTotal(),
          issPercent: taxesTitle.iss,
          inssPercent: taxesTitle.inss,
          pisPercent: taxesTitle.pis,
          cofinsPercent: taxesTitle.cofins,
          csllPercent: taxesTitle.csll,
          taxesValues: {
            cofinsValue: taxesValues.cofinsValue,
            csllValue: taxesValues.csllVaue,
            inssValue: taxesValues.inssValue,
            issValue: taxesValues.issValue,
            pisValue: taxesValues.pisValue,
          },
        };
      })
      .reverse();
  }

  private reset() {
    this._measurementsListener = undefined;
    this._measurements = [];
    this._rowsLists = [];
  }

  getMeasurements() {
    return [...this._measurements];
  }

  getMeasurementName(id: string): string {
    try {
      const measurement = this.findMeasurement(id);
      return measurement?.description || "";
    } catch (error) {}
    return "";
  }

  findMeasurement(id: string): MeasurementModel | undefined {
    return this._measurements.find((measurement) => measurement.uid === id);
  }

  subscribeMeasurements(): void {
    const contract = ContractStore.getCurrentContract();
    if (!contract) {
      throw new Error("Contract not found!");
    }

    this.unscribeMeasurements();
    this._measurementsListener =
      MeasurementService.subscribeMeasurementsSnapshot(
        contract,
        (measurements) => {
          this.addMeasurementsToList(measurements);
          measurements.forEach((measurement) =>
            MeasurementStore.updateBillingStatus(measurement)
          );
        },
        (measurements) => this.changeMeasurementsOnList(measurements),
        (measurements) => this.removeMeasurementsFromList(measurements)
      );
  }

  private addMeasurementsToList(measurementsAdded: MeasurementModel[]) {
    const measurements = measurementsAdded.concat(this._measurements);

    this.setMeasurements(measurements);
    this.updateTotalsAtContract();
  }

  private changeMeasurementsOnList(measurementsChanged: MeasurementModel[]) {
    const measurements = this._measurements;
    measurementsChanged.forEach((measure) => {
      const index = measurements.findIndex((m) => m.uid === measure.uid);
      if (index !== -1) {
        measurements[index] = measure;
      }
    });

    this.setMeasurements(measurements);
    this.updateTotalsAtContract();
  }

  removeMeasurementsFromList(measurementsRemoved: MeasurementModel[]) {
    const uids = measurementsRemoved.map((m) => m.uid);
    const measurements = this._measurements.filter(
      (m) => !uids.includes(m.uid)
    );

    this.setMeasurements(measurements);
    this.updateTotalsAtContract();
  }

  private setMeasurements(measurements: MeasurementModel[]) {
    this._measurements = measurements.sort((a, b) => a.code - b.code);
  }

  private updateTotalsAtContract() {
    let measurementsTotal = 0;
    let billingTotal = 0;

    this._measurements.forEach((measurement) => {
      measurementsTotal = decimal(measurementsTotal)
        .sum(measurement.total)
        .toNumber();

      if (measurement.status === EMeasurementStatus.FATURADA)
        billingTotal = decimal(billingTotal).sum(measurement.total).toNumber();
    });

    return ContractStore.updateContractMeasuredTotal(
      measurementsTotal,
      billingTotal
    );
  }

  unscribeMeasurements(): void {
    if (this._measurementsListener) {
      this._measurementsListener();
    }
    this.reset();
  }

  async getAllMeasurementsData() {
    const contractUid = ContractStore.getCurrentContract()?.uid;
    if (!contractUid) return;

    const measurementsCount = this._measurements.length;
    const hasAllRows = measurementsCount === this._rowsLists.length;

    if (!hasAllRows) {
      const hasAtLeastHalfOfWBS =
        measurementsCount * 0.6 < this._rowsLists.length;

      let datas: any = hasAtLeastHalfOfWBS
        ? await this.fetchSomeWBS(contractUid)
        : await this.fetchAllWBS(contractUid);

      if (datas)
        datas.forEach((data) => {
          if (data) this.createRowsList(data.uid, data.wbs);
        });
    }
  }

  private fetchAllWBS(contractUid: string) {
    return MeasurementRowService.fetchAllMeasurementsRows(contractUid);
  }

  private fetchSomeWBS(contractUid: string) {
    const uidOfExistedWBS = this._rowsLists.map((wbs) => wbs.uid);
    const measurementsMissing = this._measurements
      .filter((m) => !uidOfExistedWBS.includes(m.uid))
      .map((m) => m.uid);

    return MeasurementRowService.fetchMeasurementsRows(
      contractUid,
      measurementsMissing
    );
  }

  getRowsListByUid(
    measurementUid: string
  ): WBSModel<MeasurementRowModel> | undefined {
    return this._rowsLists.find((list) => list.uid === measurementUid);
  }

  async getAndAddRowsToList(measurementUid: string) {
    const contractUid = ContractStore.getCurrentContract()?.uid;
    if (!contractUid) return;

    const data = await MeasurementRowService.getMeasurementRows(
      contractUid,
      measurementUid
    );
    this.createRowsList(measurementUid, data);
  }

  async createRowsList(measurementUid: string, rows: MeasurementRowModel[]) {
    const newList = new WBSModel<MeasurementRowModel>(measurementUid, 1000);
    newList.addRowList(rows);
    this._rowsLists.push(newList);
  }

  private formatBillingOrderDetails(measurement: MeasurementModel) {
    if (!measurement.hasBeenSentToBilling()) return {};

    const label = "measurements.table.details";
    const billingOrder = { ...measurement.billingOrder };
    const details = {
      orderCode: "",
      orderCodeCustom: lang(`${label}.orderInProgress`),
      invoiceNumber: "",
      invoiceNumberCustom: lang(`${label}.invoiceInProgress`),
      invoiceSerial: "",
      invoiceDate: "",
      message: "",
      error: false,
    };

    if (measurement.hasBillingOrderDeleted()) {
      details.orderCodeCustom = lang(`${label}.orderDeleted`);
      details.error = true;
    } else if (measurement.hasBillingOrderError()) {
      details.orderCodeCustom = lang(`${label}.orderError`);
      details.message = billingOrder.error;
      details.error = true;
    } else if (measurement.hasBillingOrder()) {
      details.orderCode = String(billingOrder.code);
      details.orderCodeCustom = String(billingOrder.code);

      let hasInvoices = billingOrder.invoices.length;
      if (hasInvoices) {
        let formatedDate =
          billingOrder.invoices[0]?.issuanceDate.toLocaleDateString("pt-br");

        details.invoiceNumber = String(billingOrder.invoices[0].number);
        details.invoiceNumberCustom = String(billingOrder.invoices[0].number);
        details.invoiceSerial = billingOrder.invoices[0]?.serial;
        details.invoiceDate = formatedDate;
      }
    }

    return details;
  }

  taxesTitle(measurement: MeasurementModel) {
    const taxesTitle = { iss: 0, inss: 0, pis: 0, cofins: 0, csll: 0 };
    const taxes = measurement.getContract()?.taxes;

    if (taxes) {
      taxesTitle.iss = taxes.iss || 0;
      taxesTitle.inss = taxes.inss || 0;
      taxesTitle.pis = taxes.pis || 0;
      taxesTitle.cofins = taxes.cofins || 0;
      taxesTitle.csll = taxes.csll || 0;
    }

    return taxesTitle;
  }

  async updateMeasurementsDiscountsType(
    discountUpdatedId: string,
    newType: EDiscountType
  ) {
    const measurements = this._measurements.filter((measurement) =>
      [...measurement.discounts, ...measurement.billings].some(
        (d) => d.discountId === discountUpdatedId
      )
    );
    if (!measurements.length) return;

    for (const measurement of measurements) {
      await MeasurementService.updateMeasurementDiscountsType(
        measurement,
        discountUpdatedId,
        newType
      );
    }
  }

  public getTotals(rows: IMeasurementListRow[]) {
    let measurements = 0;
    let discounts = 0;
    let grossTotal = 0;
    let retentionAndAdvances = 0;
    let taxes = 0;
    let netTotal = 0;
    let measuredPercentTotal = 0;

    rows.forEach((row) => {
      measurements = sum(measurements, row.total);
      discounts = sum(discounts, row.allDiscountsTotal);
      grossTotal = sum(grossTotal, row.grossTotal);
      retentionAndAdvances = sum(retentionAndAdvances, row.retentionAdvance);
      taxes = sum(taxes, row.taxesTotal);
      netTotal = sum(netTotal, row.receivableTotal);
      measuredPercentTotal = sum(measuredPercentTotal, row.measuredPercentual);
    });

    return {
      measurements,
      discounts,
      grossTotal,
      retentionAndAdvances,
      taxes,
      netTotal,
      measuredPercentTotal,
    };
  }
}

const MeasurementListStore = new Store();
export default MeasurementListStore;
